import { useState, useRef } from 'react';
import { Card, CardHeader } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import {
  usePayrollEmployees, usePayslips, useGenerateBulkPayslips, useFinalizePayslip,
  useUpdateCompensation, useUpdatePayslip, useCompensationHistory,
  usePayrollRuns, useEnsurePayrollRun, useSubmitPayrollRun, useApprovePayrollRun, useRejectPayrollRun,
  usePayrollV2Status, useRenderedPayslip,
} from './hooks/usePayroll';
import { useMasterSalaryStructures } from '@/features/recruitment/api';
import { format } from 'date-fns';
import { FileText, CheckCircle, Printer, Users, Trash2, Edit3, Plus, Upload, Settings, ClipboardEdit, History, ShieldCheck, XCircle, ExternalLink, Download, Sliders } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import { api } from '@/shared/api/axios';
import toast from 'react-hot-toast';
import type { PayrollRun, PayrollRunUserRef } from './payrollTypes';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { PrintablePayslip } from './PrintablePayslip';
import { PayrollExcelUploadTab } from './PayrollExcelUploadTab';
import { PayrollSettingsTab } from './PayrollSettingsTab';
import { PayslipTemplateTab } from './PayslipTemplateTab';
import { PayrollMonthlyInputTab } from './PayrollMonthlyInputTab';
import type { Payslip, PayrollEmployee, PayrollBlockedEntry } from './payrollTypes';
import { formatINR } from '@/shared/utils/currency';
import { WidgetEmpty, WidgetLoading } from '../../infrastructure/widgets/WidgetStates';

export const hrPayrollConfig: WidgetConfig = {
  id: 'hr-payroll',
  title: 'Payroll Management',
  description: 'Employee payroll, compensation, and payslips',
  category: 'utility',
  refreshStrategy: 'manual',
  defaultSize: { w: 12, h: 8 },
  priority: 1,
};

// Tab order follows the actual workflow, not alphabetical/build order: set up
// compensation first, enter/import this month's numbers, review payslips,
// then submit for maker-checker approval. Settings + Template are one-time
// config (not a recurring monthly step) so they're combined into a single
// "Setup" tab at the end rather than sitting inline with the daily-use tabs.
type PayrollTabId = 'employees' | 'monthly-input' | 'upload' | 'payslips' | 'runs' | 'setup';

export function HRPayrollWidget() {
  const [activeTab, setActiveTab] = useState<PayrollTabId>('employees');
  const [setupSection, setSetupSection] = useState<'settings' | 'template'>('settings');
  const [monthYear, setMonthYear] = useState(format(new Date(), 'yyyy-MM'));
  
  const { data: employees } = usePayrollEmployees();
  const { data: payslips, isLoading: loadingPayslips } = usePayslips(monthYear);
  const { data: structures } = useMasterSalaryStructures();
  const { data: payrollRuns, isLoading: loadingRuns } = usePayrollRuns();
  const { data: v2Status } = usePayrollV2Status();
  const currentUser = useUser();

  const generateBulk = useGenerateBulkPayslips();
  const finalizeMutation = useFinalizePayslip();
  const updateComp = useUpdateCompensation();

  const updatePayslipMutation = useUpdatePayslip();
  const [editPayslipTarget, setEditPayslipTarget] = useState<Payslip | null>(null);
  const [editSlipEarnings, setEditSlipEarnings] = useState<MonthlyInputLine[]>([]);
  const [editSlipDeductions, setEditSlipDeductions] = useState<MonthlyInputLine[]>([]);

  const [assignCustomEarnings, setAssignCustomEarnings] = useState<MonthlyInputLine[]>([]);
  const [assignCustomDeductions, setAssignCustomDeductions] = useState<MonthlyInputLine[]>([]);

  const ensureRun = useEnsurePayrollRun();
  const submitRun = useSubmitPayrollRun();
  const approveRun = useApprovePayrollRun();
  const rejectRun = useRejectPayrollRun();
  const [rejectTarget, setRejectTarget] = useState<PayrollRun | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const [activePayslip, setActivePayslip] = useState<Payslip | null>(null);
  // Real payslip rendered through whatever template is currently active
  // (previously the "View" button always showed a hardcoded generic layout,
  // ignoring the uploaded template entirely — see PrintablePayslip fallback below).
  const { data: renderedPayslip, isLoading: loadingRender } = useRenderedPayslip(activePayslip?.id ?? null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
    } else {
      window.print();
    }
  };

  const handleOpenWindow = () => {
    if (renderedPayslip?.html) {
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(renderedPayslip.html);
        win.document.close();
      }
    }
  };
  const [assignTarget, setAssignTarget] = useState<PayrollEmployee | null>(null);
  const [historyTarget, setHistoryTarget] = useState<PayrollEmployee | null>(null);
  const [assignBaseSalary, setAssignBaseSalary] = useState('');
  const [assignStructure, setAssignStructure] = useState('');
  const [assignEffectiveFrom, setAssignEffectiveFrom] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [assignReason, setAssignReason] = useState('');
  // Sales BDEs often don't get incentive %/bonus set during offer-letter
  // creation — this is where HR fills the gap post-onboarding (2026-08-16).
  const [assignIncentivePercent, setAssignIncentivePercent] = useState('');
  const [assignBonus, setAssignBonus] = useState('');
  const [assignAdvanceDeduction, setAssignAdvanceDeduction] = useState('');
  const [assignPhone, setAssignPhone] = useState('');
  const [assignEmployeeId, setAssignEmployeeId] = useState('');
  const [assignDoj, setAssignDoj] = useState('');
  const [assignBankName, setAssignBankName] = useState('');
  const [assignBankAccount, setAssignBankAccount] = useState('');
  const [assignIfsc, setAssignIfsc] = useState('');
  const [assignExcludeLeaderboard, setAssignExcludeLeaderboard] = useState(false);
  const [assignExcludeIncentive, setAssignExcludeIncentive] = useState(false);

  const openAssign = (emp: PayrollEmployee) => {
    setAssignTarget(emp);
    setAssignBaseSalary(String(emp.base_salary || ''));
    setAssignStructure(emp.structure_code || '');
    setAssignEffectiveFrom(format(new Date(), 'yyyy-MM-dd'));
    setAssignReason('');
    setAssignIncentivePercent(emp.incentive_percent != null ? String(emp.incentive_percent) : '');
    setAssignBonus(emp.bonus ? String(emp.bonus) : '');
    setAssignAdvanceDeduction(emp.advance_deduction ? String(emp.advance_deduction) : '');
    setAssignPhone(emp.phone || emp.mobile || '');
    setAssignEmployeeId(emp.employee_id || '');
    setAssignDoj(emp.date_of_joining ? String(emp.date_of_joining).slice(0, 10) : '');
    setAssignBankName(emp.bank_name || '');
    setAssignBankAccount(emp.bank_account_no || '');
    setAssignIfsc(emp.bank_ifsc || '');
    setAssignExcludeLeaderboard(Boolean(emp.exclude_from_leaderboard));
    setAssignExcludeIncentive(Boolean(emp.exclude_from_incentive));
  };

  const submitAssign = () => {
    if (!assignTarget) return;
    const salary = Number(assignBaseSalary);
    if (!Number.isFinite(salary) || salary < 0) return;
    updateComp.mutate({
      user_id: assignTarget.id, base_salary: salary, effective_from: assignEffectiveFrom,
      structure_code: assignStructure || null, change_reason: assignReason.trim(),
      incentive_percent: assignIncentivePercent.trim() === '' ? null : Number(assignIncentivePercent),
      bonus: assignBonus.trim() === '' ? 0 : Number(assignBonus),
      advance_deduction: assignAdvanceDeduction.trim() === '' ? 0 : Number(assignAdvanceDeduction),
      phone: assignPhone.trim(),
      employee_id: assignEmployeeId.trim(),
      date_of_joining: assignDoj || null,
      bank_name: assignBankName.trim(),
      bank_account_no: assignBankAccount.trim(),
      bank_ifsc: assignIfsc.trim(),
      exclude_from_leaderboard: assignExcludeLeaderboard,
      exclude_from_incentive: assignExcludeIncentive,
    }, {
      onSuccess: () => {
        toast.success('Compensation & employee details updated');
        setAssignTarget(null);
      }
    });
  };

  
  const openEditPayslip = (p: Payslip) => {
    setEditPayslipTarget(p);
    setEditSlipEarnings((p.earnings || []).length > 0 ? p.earnings.map(e => ({ ...e })) : DEFAULT_ANNEXURE_EARNINGS.map(e => ({ ...e })));
    setEditSlipDeductions((p.deductions || []).length > 0 ? p.deductions.map(d => ({ ...d })) : DEFAULT_ANNEXURE_DEDUCTIONS.map(d => ({ ...d })));
  };

  const submitEditPayslip = async () => {
    if (!editPayslipTarget) return;
    try {
      await updatePayslipMutation.mutateAsync({
        id: editPayslipTarget.id,
        earnings: editSlipEarnings.filter(e => e.label.trim()),
        deductions: editSlipDeductions.filter(d => d.label.trim()),
      });
      toast.success('Salary slip components updated successfully');
      setEditPayslipTarget(null);
    } catch {
      toast.error('Failed to update salary slip components');
    }
  };

  const handleDownloadPdfDirect = async (p: Payslip | null) => {
    if (!p) return;
    try {
      const response = await api.get(`/api/v2/hr/payroll/pdf/${p.id}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const empName = (p.user?.display_name || 'Employee').replace(/[^a-zA-Z0-9_-]/g, '_');
      link.setAttribute('download', `Salary_Slip_${empName}_${p.month_year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Salary slip downloaded');
    } catch (err) {
      if (renderedPayslip?.html) {
        handleOpenWindow();
      } else {
        toast.error('Could not download PDF directly, opening print view');
        setActivePayslip(p);
      }
    }
  };

  const historyQuery = useCompensationHistory(historyTarget?.id ?? null);
  // Employees generate-bulk couldn't produce a payslip for (no compensation
  // set) — surfaced after the run instead of silently vanishing (2026-08-16).
  const [blockedResult, setBlockedResult] = useState<PayrollBlockedEntry[] | null>(null);

  const handleGenerate = async () => {
    if (confirm(`Generate draft payslips for ${monthYear}?`)) {
      const result = await generateBulk.mutateAsync(monthYear);
      if (result.blocked && result.blocked.length > 0) setBlockedResult(result.blocked);
    }
  };

  // Submit this month's draft payslips for a second person's sign-off — a
  // run is get-or-created lazily (rather than eagerly for every month) so
  // this stays a single click even the first time HR touches a given month.
  const handleSubmitRun = async () => {
    const run = await ensureRun.mutateAsync(monthYear);
    await submitRun.mutateAsync(run.id);
  };

  const handleApproveRun = (run: PayrollRun) => {
    if (confirm(`Approve and finalize ${run.payslip_count} payslip(s) for ${run.month_year}? This posts them to accounting and cannot be undone.`)) {
      approveRun.mutate(run.id);
    }
  };

  const openReject = (run: PayrollRun) => {
    setRejectTarget(run);
    setRejectReason('');
  };

  const submitReject = () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    rejectRun.mutate({ id: rejectTarget.id, reason: rejectReason.trim() }, { onSuccess: () => setRejectTarget(null) });
  };

  const userRefId = (ref?: PayrollRunUserRef | string | null): string | null => {
    if (!ref) return null;
    return typeof ref === 'string' ? ref : ref.id ?? null;
  };
  const userRefName = (ref?: PayrollRunUserRef | string | null): string => {
    if (!ref || typeof ref === 'string') return '—';
    return ref.display_name || ref.username || '—';
  };
  // Same segregation-of-duty rule as the backend (payroll-run-policy.js) —
  // mirrored here only to disable/hide the button proactively; the server
  // re-checks this on every request regardless, so this is UX, not the guard.
  const canDecide = (run: PayrollRun) => {
    if (currentUser?.role === 'superadmin') return true;
    return userRefId(run.submitted_by) !== currentUser?.id;
  };

  const currentMonthRun = payrollRuns?.find((r) => r.month_year === monthYear) || null;

  return (
    <Card className="col-span-full shadow-xs rounded-lg border border-border">
      <CardHeader 
        title={<span className="text-xl font-semibold text-on-surface flex items-center gap-2">Payroll Management</span>}
        subtitle="Manage employee compensations, upload payroll sheets, and generate salary slips."
        className="border-b border-outline pb-4"
      />
      
      <div className="w-full">
        <div className="border-b border-outline px-6 py-2 bg-surface-variant flex gap-3 flex-wrap items-center">
          <button
            className={cn("px-4 py-2 flex items-center rounded-md font-medium text-body-md transition-colors", activeTab === 'employees' ? "bg-primary text-on-primary" : "text-on-surface-variant hover:text-on-surface")}
            onClick={() => setActiveTab('employees')}
          >
            <Users className="w-4 h-4 mr-2" /> Employee Compensation
          </button>
          <button
            className={cn("px-4 py-2 flex items-center rounded-md font-medium text-body-md transition-colors", activeTab === 'monthly-input' ? "bg-primary text-on-primary" : "text-on-surface-variant hover:text-on-surface")}
            onClick={() => setActiveTab('monthly-input')}
          >
            <ClipboardEdit className="w-4 h-4 mr-2" /> Monthly Input
          </button>
          <button
            className={cn("px-4 py-2 flex items-center rounded-md font-medium text-body-md transition-colors", activeTab === 'upload' ? "bg-primary text-on-primary" : "text-on-surface-variant hover:text-on-surface")}
            onClick={() => setActiveTab('upload')}
          >
            <Upload className="w-4 h-4 mr-2" /> Upload Sheet
          </button>
          <button
            className={cn("px-4 py-2 flex items-center rounded-md font-medium text-body-md transition-colors", activeTab === 'payslips' ? "bg-primary text-on-primary" : "text-on-surface-variant hover:text-on-surface")}
            onClick={() => setActiveTab('payslips')}
          >
            <FileText className="w-4 h-4 mr-2" /> Salary Slips
          </button>
          <button
            className={cn("px-4 py-2 flex items-center rounded-md font-medium text-body-md transition-colors", activeTab === 'runs' ? "bg-primary text-on-primary" : "text-on-surface-variant hover:text-on-surface")}
            onClick={() => setActiveTab('runs')}
          >
            <ShieldCheck className="w-4 h-4 mr-2" /> Payroll Runs
          </button>
          <span className="w-px h-6 bg-outline mx-1" aria-hidden="true" />
          <button
            className={cn("px-4 py-2 flex items-center rounded-md font-medium text-body-md transition-colors", activeTab === 'setup' ? "bg-primary text-on-primary" : "text-on-surface-variant hover:text-on-surface")}
            onClick={() => setActiveTab('setup')}
          >
            <Settings className="w-4 h-4 mr-2" /> Setup
          </button>
        </div>

        {activeTab === 'payslips' && (
          <div className="p-6 m-0 outline-none">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <Input type="month" value={monthYear} onChange={e => setMonthYear(e.target.value)} className="bg-surface border-outline text-on-surface" />
              </div>
              <Button onClick={handleGenerate} disabled={generateBulk.isPending} variant="primary">
                {generateBulk.isPending ? 'Generating...' : 'Generate Drafts'}
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
              {loadingPayslips ? (
                <WidgetLoading variant="table" />
              ) : payslips?.length === 0 ? (
                <WidgetEmpty message={`No salary slip has been generated for ${monthYear}. Use Generate drafts to create them for this month.`} />
              ) : (
                <table className="w-full text-body-md text-left">
                  <thead className="sticky top-0 z-10 bg-muted text-label-caps uppercase tracking-wider text-on-surface-variant">
                    <tr>
                      <th className="px-6 py-3 font-medium">Employee</th>
                      <th className="px-6 py-3 font-medium">Month</th>
                      <th className="px-6 py-3 font-medium">Net Salary</th>
                      <th className="px-6 py-3 font-medium">Status</th>
                      <th className="px-6 py-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline/50">
                    {payslips?.map((p) => (
                      <tr key={p.id} className="hover:bg-surface-variant/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-medium text-on-surface">{p.user?.display_name}</div>
                          <div className="text-body-sm text-on-surface-variant">{p.user?.position || p.user?.department}</div>
                        </td>
                        <td className="px-6 py-4 text-on-surface">{p.month_year}</td>
                        <td className="px-6 py-4 font-semibold text-success-text ">{formatINR(p.net_salary)}</td>
                        <td className="px-6 py-4">
                          {p.status === 'finalized' ? (
                            <span className="text-body-sm bg-success/10 text-success-text border border-success/30 dark:bg-emerald-900/30  px-2 py-1 rounded-full dark:border-emerald-800/50">Finalized</span>
                          ) : (
                            <span className="text-body-sm bg-warning/10 text-warning-text border border-warning/30 dark:bg-amber-900/30 dark:text-warning-text px-2 py-1 rounded-full dark:border-amber-800/50">Draft</span>
                          )}
                        </td>
                        <td className="font-mono px-6 py-4 text-right space-x-2 tabular-nums text-data-sm">
                          <Button variant="secondary" size="sm" onClick={() => setActivePayslip(p)} className="text-info-text hover:text-info-text hover:bg-info/10 dark:text-info-text dark:hover:text-info-text dark:hover:bg-blue-900/30">
                            <FileText className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => handleDownloadPdfDirect(p)} title="Download PDF Salary Slip" className="text-emerald-700 hover:bg-emerald-50 border border-emerald-200">
                            <Download className="w-4 h-4 mr-1" /> PDF
                          </Button>
                          {p.status === 'draft' && (
                            <Button variant="secondary" size="sm" onClick={() => finalizeMutation.mutate(p.id)} disabled={finalizeMutation.isPending} className="text-success-text hover:bg-success/10 dark:border-emerald-700 dark:text-success-text dark:hover:bg-emerald-900/30">
                              <CheckCircle className="w-4 h-4 mr-1" /> Finalize
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {activeTab === 'employees' && (
          <div className="p-6 m-0 outline-none">
            <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
              <table className="w-full text-body-md text-left">
                <thead className="text-body-sm uppercase bg-surface-variant text-on-surface-variant sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 font-medium">Employee</th>
                    <th className="px-6 py-3 font-medium">Department / Role</th>
                    <th className="px-6 py-3 font-medium">Base Salary</th>
                    <th className="px-6 py-3 font-medium">Salary Structure</th>
                    <th className="px-6 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline/50">
                  {employees?.map((emp) => (
                    <tr key={emp.id} className="hover:bg-surface-variant/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center flex-wrap gap-1">
                          <span className="font-medium text-on-surface">{emp.display_name}</span>
                          {(emp.exclude_from_leaderboard || emp.username === 'bdm6') && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium border border-amber-300">No Leaderboard</span>
                          )}
                          {(emp.exclude_from_incentive || emp.username === 'bdm6') && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-medium border border-purple-300">No Incentive</span>
                          )}
                        </div>
                        <div className="text-body-sm text-on-surface-variant">{emp.email}</div>
                      </td>
                      <td className="px-6 py-4 text-on-surface">
                        {emp.department} {emp.position ? `- ${emp.position}` : ''}
                      </td>
                      <td className="px-6 py-4 font-semibold text-success-text ">
                        {formatINR(emp.base_salary || 0)}
                      </td>
                      <td className="px-6 py-4 text-on-surface">
                        {emp.structure_name ? (
                          <span className="text-body-sm bg-primary/10 text-primary px-2 py-1 rounded-full">{emp.structure_name}</span>
                        ) : (
                          <span className="text-body-sm text-on-surface-variant italic">Not assigned</span>
                        )}
                      </td>
                      <td className="font-mono px-6 py-4 text-right space-x-2 tabular-nums text-data-sm">
                        <Button size="sm" variant="secondary" className="h-8 border-outline" onClick={() => openAssign(emp)}>
                          Assign
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8" onClick={() => setHistoryTarget(emp)} title="History">
                          <History className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'runs' && (
          <div className="p-6 m-0 outline-none space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-on-surface">Maker-checker approval</h3>
                <p className="text-body-sm text-on-surface-variant mt-1 max-w-xl">
                  A month's draft payslips must be submitted by one HR user and approved by a <em>different</em> HR user (or a superadmin) before they're finalized and posted to accounting.
                </p>
              </div>
              <Input type="month" value={monthYear} onChange={e => setMonthYear(e.target.value)} className="bg-surface border-outline text-on-surface" />
            </div>

            {/* Structure-engine adoption — the visibility needed before the
                legacy 50/20/30 formula can safely be retired (plan step 9). */}
            {v2Status && (
              <div className="flex items-center gap-4 text-body-sm border border-border rounded-lg p-3 bg-surface-variant/30">
                <span className={cn('px-2 py-1 rounded-full border font-medium', v2Status.flagEnabled ? 'bg-success/10 text-success-text border-success/30 dark:bg-emerald-900/30  dark:border-emerald-800/50' : 'bg-destructive-subtle text-danger-text border-danger/30 dark:bg-red-900/30 dark:text-danger-text dark:border-red-800/50')}>
                  Structure engine: {v2Status.flagEnabled ? 'ON' : 'OFF'}
                </span>
                <span className="text-on-surface-variant">
                  <span className="text-on-surface font-medium">{v2Status.adoptionPercent}%</span> of active employees ({v2Status.employeesOnStructure}/{v2Status.totalActiveEmployees}) are on a salary structure
                  {v2Status.employeesUnassigned > 0 && <> — {v2Status.employeesUnassigned} still on the legacy formula</>}
                </span>
              </div>
            )}

            {/* Current month's status + primary action */}
            <div className="border border-border rounded-lg p-4 bg-surface-variant/40">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-body-md font-medium text-on-surface">{monthYear}</div>
                  <div className="text-body-sm text-on-surface-variant mt-0.5">
                    {currentMonthRun ? <RunStatusBadge status={currentMonthRun.status} /> : <span className="italic">No run started yet</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(!currentMonthRun || currentMonthRun.status === 'draft' || currentMonthRun.status === 'rejected') && (
                    <Button size="sm" onClick={handleSubmitRun} disabled={ensureRun.isPending || submitRun.isPending}>
                      {submitRun.isPending || ensureRun.isPending ? 'Submitting...' : currentMonthRun?.status === 'rejected' ? 'Resubmit for Approval' : 'Submit for Approval'}
                    </Button>
                  )}
                  {currentMonthRun?.status === 'submitted' && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleApproveRun(currentMonthRun)}
                        disabled={!canDecide(currentMonthRun) || approveRun.isPending}
                        title={!canDecide(currentMonthRun) ? 'You submitted this run — a different HR user or superadmin must approve it.' : undefined}
                        className="text-success-text border-success/30 hover:bg-success/10  dark:hover:bg-emerald-900/30"
                        variant="secondary"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Approve &amp; Finalize
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openReject(currentMonthRun)}
                        disabled={!canDecide(currentMonthRun) || rejectRun.isPending}
                        title={!canDecide(currentMonthRun) ? 'You submitted this run — a different HR user or superadmin must decide on it.' : undefined}
                        className="text-danger-text border-danger/30 hover:bg-destructive-subtle dark:text-danger-text dark:hover:bg-red-900/30"
                        variant="secondary"
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {currentMonthRun && currentMonthRun.status !== 'draft' && (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-body-sm text-on-surface-variant">
                  <div><span className="block text-on-surface font-medium">{currentMonthRun.payslip_count}</span>Payslips</div>
                  <div><span className="block text-on-surface font-medium">{formatINR(currentMonthRun.total_net)}</span>Total Net</div>
                  <div><span className="block text-on-surface font-medium">{userRefName(currentMonthRun.submitted_by)}</span>Submitted By</div>
                  <div><span className="block text-on-surface font-medium">{userRefName(currentMonthRun.approved_by) !== '—' ? userRefName(currentMonthRun.approved_by) : userRefName(currentMonthRun.rejected_by)}</span>{currentMonthRun.status === 'rejected' ? 'Rejected By' : 'Approved By'}</div>
                </div>
              )}
              {currentMonthRun?.status === 'rejected' && currentMonthRun.rejection_reason && (
                <div className="mt-3 text-body-sm bg-destructive-subtle text-danger-text border border-danger/30 dark:bg-red-900/20 dark:text-danger-text dark:border-red-800/50 rounded-md p-2">
                  <span className="font-medium">Rejection reason:</span> {currentMonthRun.rejection_reason}
                </div>
              )}
            </div>

            {/* History across all months */}
            <div>
              <h4 className="text-body-sm font-semibold uppercase text-on-surface-variant mb-2">All Runs</h4>
              <div className="max-h-[300px] overflow-y-auto custom-scrollbar border border-border rounded-lg">
                {loadingRuns ? (
                  <div className="p-6 text-center text-on-surface-variant text-body-md">Loading...</div>
                ) : !payrollRuns?.length ? (
                  <div className="p-6 text-center text-on-surface-variant text-body-md">No payroll runs yet.</div>
                ) : (
                  <table className="w-full text-body-md text-left">
                    <thead className="text-body-sm uppercase bg-surface-variant text-on-surface-variant sticky top-0">
                      <tr>
                        <th className="px-4 py-2 font-medium">Month</th>
                        <th className="px-4 py-2 font-medium">Status</th>
                        <th className="px-4 py-2 font-medium">Payslips</th>
                        <th className="px-4 py-2 font-medium">Total Net</th>
                        <th className="px-4 py-2 font-medium">Submitted By</th>
                        <th className="px-4 py-2 font-medium">Decided By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline/50">
                      {payrollRuns.map((r) => (
                        <tr key={r.id} className="hover:bg-surface-variant/50">
                          <td className="px-4 py-2 text-on-surface">{r.month_year}</td>
                          <td className="px-4 py-2"><RunStatusBadge status={r.status} /></td>
                          <td className="px-4 py-2 text-on-surface">{r.payslip_count}</td>
                          <td className="px-4 py-2 text-on-surface">{formatINR(r.total_net)}</td>
                          <td className="px-4 py-2 text-on-surface-variant">{userRefName(r.submitted_by)}</td>
                          <td className="px-4 py-2 text-on-surface-variant">{r.status === 'rejected' ? userRefName(r.rejected_by) : userRefName(r.approved_by)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'upload' && (
          <div className="p-6 m-0 outline-none">
            <PayrollExcelUploadTab />
          </div>
        )}

        {activeTab === 'monthly-input' && (
          <PayrollMonthlyInputTab />
        )}

        {activeTab === 'setup' && (
          <div className="p-6 m-0 outline-none">
            <div className="flex gap-2 mb-6 border-b border-outline pb-3">
              <button
                className={cn("px-3 py-1.5 rounded-md text-body-md font-medium transition-colors", setupSection === 'settings' ? "bg-primary/10 text-primary" : "text-on-surface-variant hover:text-on-surface")}
                onClick={() => setSetupSection('settings')}
              >
                Company Settings
              </button>
              <button
                className={cn("px-3 py-1.5 rounded-md text-body-md font-medium transition-colors", setupSection === 'template' ? "bg-primary/10 text-primary" : "text-on-surface-variant hover:text-on-surface")}
                onClick={() => setSetupSection('template')}
              >
                Salary Slip Template
              </button>
            </div>
            {setupSection === 'settings' ? <PayrollSettingsTab /> : <PayslipTemplateTab />}
          </div>
        )}
      </div>

      <Modal open={!!activePayslip} onClose={() => setActivePayslip(null)} title={
        <div className="flex justify-between items-center pr-8 w-full">
          <span className="font-semibold text-slate-800">Salary Slip Document</span>
          <div className="flex items-center gap-2">
            <Button onClick={() => handleDownloadPdfDirect(activePayslip)} variant="primary" className="gap-2 border-0 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm">
              <Download className="w-3.5 h-3.5" /> Download PDF
            </Button>
            {renderedPayslip?.html && (
              <Button onClick={handleOpenWindow} variant="secondary" className="gap-2 text-xs">
                <ExternalLink className="w-3.5 h-3.5" /> Open in New Tab
              </Button>
            )}
            <Button onClick={handlePrint} variant="secondary" className="gap-2 border text-xs">
              <Printer className="w-3.5 h-3.5" /> Print
            </Button>
          </div>
        </div>
      } size="lg">
        <div className="overflow-x-auto p-4 bg-surface-variant rounded-lg max-h-[70vh] overflow-y-auto">
          {loadingRender ? (
            <div className="p-8 text-center text-on-surface-variant text-body-md">Loading...</div>
          ) : renderedPayslip?.html ? (
            <iframe
              ref={iframeRef}
              srcDoc={renderedPayslip.html}
              title="Payslip"
              className="w-full min-h-[650px] border-0 bg-white rounded shadow-sm"
              sandbox="allow-same-origin allow-modals"
            />
          ) : (
            // No template has been activated yet (Setup → Payslip Template) — fall
            // back to the generic layout rather than showing a blank modal.
            activePayslip && <PrintablePayslip payslip={activePayslip} />
          )}
        </div>
      </Modal>

      {/* Assign salary structure / update base pay — writes a new effective-dated
          Compensation row (never edits an old one), so history stays intact. */}
      <Modal
        open={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        title={assignTarget ? `Compensation & Employee Details — ${assignTarget.display_name}` : 'Compensation & Employee Details'}
        size="md"
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setAssignTarget(null)}>Cancel</Button>
            <Button onClick={submitAssign} disabled={updateComp.isPending || !assignBaseSalary} variant="primary">
              {updateComp.isPending ? 'Saving...' : 'Save Details'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="p-3 bg-red-50/60 border border-red-200/80 rounded-md">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-semibold text-red-800 uppercase tracking-wider">Salary &amp; Structure Configuration</span>
              <a
                href="/master-data?key=salary-structures"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-red-700 hover:text-red-900 font-medium underline flex items-center gap-1"
              >
                <Sliders className="w-3 h-3" /> Configure Structures in Master Data &rarr;
              </a>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Base Salary (₹) *"
                type="number"
                value={assignBaseSalary}
                onChange={(e) => setAssignBaseSalary(e.target.value)}
              />
              <Select
                label="Salary Structure"
                placeholder="Select Salary Structure..."
                options={(structures || []).filter((s) => s.active !== false).map((s) => ({ label: s.name, value: s.code }))}
                value={assignStructure}
                onChange={(e) => setAssignStructure(e.target.value)}
              />
            </div>
            <div className="mt-3">
              <Input
                label="Monthly Advance Salary Deduction (₹)"
                type="number"
                min={0}
                placeholder="e.g. 2000 (deducted monthly from salary)"
                value={assignAdvanceDeduction}
                onChange={(e) => setAssignAdvanceDeduction(e.target.value)}
              />
              <span className="text-[11px] text-slate-500 mt-0.5 block">
                If the employee has taken advance salary, enter the monthly deduction amount here to automatically deduct it.
              </span>
            </div>
          </div>

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider block mb-2">Employee Identification &amp; Contact</span>
            <div className="grid grid-cols-3 gap-3">
              <Input
                label="Mobile Number"
                type="text"
                placeholder="e.g. 9146588595"
                value={assignPhone}
                onChange={(e) => setAssignPhone(e.target.value)}
              />
              <Input
                label="Employee ID / Code"
                type="text"
                placeholder="e.g. RBM001"
                value={assignEmployeeId}
                onChange={(e) => setAssignEmployeeId(e.target.value)}
              />
              <Input
                label="Date of Joining"
                type="date"
                value={assignDoj}
                onChange={(e) => setAssignDoj(e.target.value)}
              />
            </div>
          </div>

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-md">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider block mb-2">Bank Account for Salary Credit</span>
            <div className="grid grid-cols-3 gap-3">
              <Input
                label="Bank Name"
                placeholder="e.g. HDFC Bank, SBI"
                value={assignBankName}
                onChange={(e) => setAssignBankName(e.target.value)}
              />
              <Input
                label="Bank Account No"
                placeholder="e.g. 50100234567890"
                value={assignBankAccount}
                onChange={(e) => setAssignBankAccount(e.target.value)}
              />
              <Input
                label="IFSC Code"
                placeholder="e.g. HDFC0001234"
                value={assignIfsc}
                onChange={(e) => setAssignIfsc(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Effective From"
              type="date"
              value={assignEffectiveFrom}
              onChange={(e) => setAssignEffectiveFrom(e.target.value)}
            />
            <Input
              label="Change Reason"
              placeholder="e.g. Annual revision, salary advance setup"
              value={assignReason}
              onChange={(e) => setAssignReason(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Standing Incentive %"
              type="number"
              min={0}
              max={100}
              placeholder="e.g. 5 (sales only)"
              value={assignIncentivePercent}
              onChange={(e) => setAssignIncentivePercent(e.target.value)}
            />
            <Input
              label="Recurring Monthly Bonus (₹)"
              type="number"
              min={0}
              placeholder="e.g. 1000"
              value={assignBonus}
              onChange={(e) => setAssignBonus(e.target.value)}
            />
          </div>

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-md space-y-2">
            <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider block">Policy &amp; Performance Exceptions</span>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                  checked={assignExcludeLeaderboard}
                  onChange={(e) => setAssignExcludeLeaderboard(e.target.checked)}
                />
                Exclude from Sales Leaderboard
              </label>
              <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-primary focus:ring-primary h-4 w-4"
                  checked={assignExcludeIncentive}
                  onChange={(e) => setAssignExcludeIncentive(e.target.checked)}
                />
                Exclude from Commission / Incentive
              </label>
            </div>
            <span className="text-[11px] text-slate-500 block">
              Used for employees like Rukmani ji (superadmin portfolio managers) to measure internal performance without public leaderboard ranking or direct commission payout.
            </span>
          </div>
        </div>
      </Modal>

      {/* Compensation / structure assignment history for one employee. */}
      <Modal
        open={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        title={historyTarget ? `Compensation History — ${historyTarget.display_name}` : 'Compensation History'}
        size="md"
      >
        {historyQuery.isLoading ? (
          <div className="p-4 text-center text-on-surface-variant text-body-md">Loading...</div>
        ) : !historyQuery.data?.length ? (
          <div className="p-4 text-center text-on-surface-variant text-body-md">No compensation history yet.</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-outline/50">
            {historyQuery.data.map((row) => (
              <div key={row.id} className="py-3 text-body-md">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-on-surface">{formatINR(row.base_salary)}</span>
                  <span className="text-body-sm text-on-surface-variant">
                    Effective {format(new Date(row.effective_from), 'dd MMM yyyy')}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-body-sm text-on-surface-variant">
                  <span>{row.structure_name || 'No structure assigned'}</span>
                  {row.change_reason && <span>&middot; {row.change_reason}</span>}
                </div>
                {(row.incentive_percent != null || row.bonus > 0) && (
                  <div className="mt-0.5 flex items-center gap-2 text-body-sm text-on-surface-variant">
                    {row.incentive_percent != null && <span>Incentive {row.incentive_percent}%</span>}
                    {row.bonus > 0 && <span>&middot; Bonus {formatINR(row.bonus)}</span>}
                  </div>
                )}
                {row.created_by_name && (
                  <div className="mt-0.5 text-body-sm text-on-surface-variant">Set by {row.created_by_name}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Employees blocked from this generate-bulk run because they have no
          compensation record — non-blocking for everyone else, but HR needs
          to see who was skipped and why instead of a silent gap. */}
      <Modal
        open={!!blockedResult}
        onClose={() => setBlockedResult(null)}
        title={`${blockedResult?.length || 0} Employee(s) Blocked — No Compensation Set`}
        size="sm"
      >
        <div className="space-y-3">
          <p className="text-body-md text-on-surface-variant">
            These employees have no base salary on file, so no payslip could be generated for them this run.
            Assign a Compensation record (Employee Compensation tab) and re-run generate for {monthYear}.
          </p>
          <div className="max-h-[40vh] overflow-y-auto divide-y divide-outline/50">
            {(blockedResult || []).map((b) => (
              <div key={b.userId} className="py-2 text-body-md">
                <div className="font-medium text-on-surface">{b.name}</div>
                <div className="text-body-sm text-on-surface-variant">{b.reason}</div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* Reject a submitted payroll run — requires a reason so the maker
          knows what to fix before resubmitting. */}
      <Modal
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        title={rejectTarget ? `Reject Payroll Run — ${rejectTarget.month_year}` : 'Reject Payroll Run'}
        size="sm"
        footer={
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button onClick={submitReject} disabled={rejectRun.isPending || !rejectReason.trim()}>
              {rejectRun.isPending ? 'Rejecting...' : 'Reject Run'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Reason *"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Base salary for two employees looks wrong, please recheck"
          />
          <p className="text-body-sm text-on-surface-variant">
            The month's payslips stay as drafts — HR can fix them and resubmit for another review.
          </p>
        </div>
      </Modal>
    </Card>
  );
}

/** Small status pill for a PayrollRun — kept local, only used on this widget's Runs tab. */
function RunStatusBadge({ status }: { status: PayrollRun['status'] }) {
  const styles: Record<PayrollRun['status'], string> = {
    draft: 'bg-surface-variant text-on-surface-variant border-outline',
    submitted: 'bg-warning/10 text-warning-text border-warning/30 dark:bg-amber-900/30 dark:text-warning-text dark:border-amber-800/50',
    approved: 'bg-success/10 text-success-text border-success/30 dark:bg-emerald-900/30  dark:border-emerald-800/50',
    finalized: 'bg-success/10 text-success-text border-success/30 dark:bg-emerald-900/30  dark:border-emerald-800/50',
    rejected: 'bg-destructive-subtle text-danger-text border-danger/30 dark:bg-red-900/30 dark:text-danger-text dark:border-red-800/50',
  };
  const labels: Record<PayrollRun['status'], string> = {
    draft: 'Draft', submitted: 'Pending Approval', approved: 'Approved', finalized: 'Finalized', rejected: 'Rejected',
  };
  return <span className={cn('text-body-sm px-2 py-1 rounded-full border', styles[status])}>{labels[status]}</span>;
}
